import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { AdherentService } from '../shared/services/adherent.service';
import { UserSessionService } from '../shared/services/user.service';
import { Router } from '@angular/router';
import { Subject, takeUntil, tap, filter, take } from 'rxjs';
import { Adherent } from '../shared/interfaces/adherents/adherents.model';
import { UpperCasePipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import {
  MatFormField,
  MatPrefix,
  MatSuffix,
} from '@angular/material/form-field';
import { MatToolbar } from '@angular/material/toolbar';
import { FilialeSelectorComponent } from '../shared/components/filiale-selector/filiale-selector.component';

@Component({
  selector: 'app-adherents',
  templateUrl: './adherents.component.html',
  styleUrl: './adherents.component.scss',
  standalone: true,
  imports: [
    MatToolbar,
    FilialeSelectorComponent,
    MatFormField,
    MatIcon,
    MatPrefix,
    MatInput,
    FormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    MatPaginator,
    UpperCasePipe,
  ],
})
export class AdherentsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private readonly KEY_CURRENT_USER = 'currentUserAdmin';
  public readonly NO_DATA = 'Aucune donnée correspondant au filtre';
  // Table
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  displayedColumns: string[] = ['nom', 'cp', 'ville', 'actions'];
  dataSource = new MatTableDataSource<Adherent>();
  // Filter
  globalFilter = '';
  // list adhérents
  listAdherents!: Adherent[];
  private allAdherents: Adherent[] = [];
  // Current user
  currentUser: any;
  activiteAdherent: string = '';

  constructor(
    private readonly router: Router,
    private readonly adherentService: AdherentService,
    private readonly userSessionService: UserSessionService,
  ) {}

  ngOnInit(): void {
    this.onTabSelected(1);

    // Subscribe to activite$ to handle the case where activite is set asynchronously
    this.userSessionService.activite$
      .pipe(
        takeUntil(this._destroying$),
        filter((activite) => activite !== null), // Wait for a non-null value
        take(1), // Only take the first non-null value
      )
      .subscribe((activite) => {
        this.activiteAdherent = activite;
        // Read currentUser here, after app.component has stored it in localStorage
        this.currentUser = JSON.parse(
          localStorage[this.KEY_CURRENT_USER] ?? '{}',
        );

        // For GPO users, filter adherents by those linked to the user
        if (this.activiteAdherent === 'gpo' && this.currentUser?.email) {
          this.fetchAdherentsByUser(this.currentUser.email);
          this.userSessionService.filiale$
            .pipe(takeUntil(this._destroying$))
            .subscribe((filiale) => {
              this.filterByFiliale(filiale);
            });
        } else {
          this.fetchAllAdherent();
        }
      });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves the list of all adherents via the adherentService service.
   * The data is stored in the listAdherents property.
   */
  private fetchAllAdherent() {
    this.adherentService
      .fetchAllAdherent()
      .pipe(
        takeUntil(this._destroying$),
        tap((data: Adherent[]) => {
          this.listAdherents = data;
          this.manageDataSourceTable(this.listAdherents);
        }),
      )
      .subscribe();
  }

  /**
   * Retrieves adherents linked to a specific user (for GPO users).
   * Once loaded, applies the current filiale filter.
   * @param email The user's email address
   */
  private fetchAdherentsByUser(email: string) {
    this.userSessionService
      .getOfficine(email, 'adherents')
      .pipe(
        takeUntil(this._destroying$),
        tap((data: Adherent[]) => {
          this.allAdherents = data;
          this.userSessionService.filiale$
            .pipe(take(1))
            .subscribe((filiale) => {
              this.filterByFiliale(filiale);
            });
        }),
      )
      .subscribe();
  }

  /**
   * Sets the array of data as the source for a data table called "dataSource".
   * @param data An array of data to set as the source for the data table.
   */
  private manageDataSourceTable(data: Adherent[]) {
    if (data) {
      this.dataSource.data = data;
    }
  }

  /**
   * Manages the filters to apply for the search bar
   * If the table has pagination then the pagination is placed on the first page
   * @param filter The value of the applied filter
   */
  applyFilter(event: string) {
    this.globalFilter = event;
    this.dataSource.filter = this.globalFilter.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  /**
   * navigate to adherents-B2C view
   * @param row Data of a selected row
   */
  goToAdherentB2C(row: Adherent) {
    this.router.navigate(['adherents/adherents-user'], {
      state: { data: row },
    });
  }

  private filterByFiliale(filiale: string | null): void {
    if (filiale) {
      this.listAdherents = this.allAdherents.filter(
        (adherent) => adherent.filiale === filiale,
      );
    } else {
      this.listAdherents = [];
    }
    this.manageDataSourceTable(this.listAdherents);
  }

  /**
   * Highlights the selected tab and sets the others to the default state
   * @param {number} index - The index of the selected tab
   */
  onTabSelected(index: number): void {
    const menuElements = document.querySelectorAll('.menu-element');
    if (menuElements.length > 3 && index >= 0 && index < menuElements.length) {
      for (let i = 0; i < menuElements.length; i++) {
        const menuElement = menuElements[i] as HTMLElement;
        if (i === index) {
          menuElement.classList.remove('menu-element--default');
          menuElement.classList.add('menu-element--highlighted');
        } else {
          menuElement.classList.remove('menu-element--highlighted');
          menuElement.classList.add('menu-element--default');
        }
      }
    }
  }
}
